import { SearchOutlined } from '@ant-design/icons';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type ICategoryEbay,
  type IEbayMarketplace,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import {
  type IEbayCategoryNode,
  type IEbayCategoryTreeNodeAncestor,
} from '@mahawi/eshop-rabbit';
import { type RootState } from '@reducers';
import { Button, Col, Form, Input, Row, Select, Skeleton } from 'antd';
import { type DefaultOptionType } from 'antd/lib/select';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ENotificationSeverity } from '../../../admin-types';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type NotifyType, useNotify } from '../../notification';
import {
  ebayCategoryLoad,
  ebayCategorySave,
  ebayCategorySuggestions,
} from '../../reducers/ebay/reducer';
import { SPACING } from '../../theme/tokens';
import Link from '../link';

interface IFormSearch {
  categoryName: string;
  marketplace: IEbayMarketplace;
}

interface IFormAssign {
  ebayCategoryId: string;
}

function EbayCategories(): React.ReactElement {
  const notify: NotifyType = useNotify();
  const dispatch = useAppDispatch();
  const marketplaces = useSelector((s: RootState) => s.Ebay.marketplaces);
  const ebayCategories = useSelector((s: RootState) => s.Ebay.categories);
  const ebayCategorySuggestionsState = useSelector(
    (s: RootState) => s.Ebay.categorySuggestions,
  );
  const ebayInProcess = useSelector((s: RootState) => s.Ebay.inProcess);
  const category = useSelector((s: RootState) => s.Category.category);

  const [formSearch] = Form.useForm<IFormSearch>();
  const [formAssign] = Form.useForm<IFormAssign>();

  const [marketplacesElements, setMarketplacesElements] =
    useState<React.ReactElement[]>();

  const onFinish = useCallback(
    (marketplace: IEbayMarketplace, { ebayCategoryId }: IFormAssign): void => {
      if (!category) {
        return;
      }

      dispatch(
        ebayCategorySave(category.uuid, marketplace.code, ebayCategoryId),
      );
      notify(ENotificationSeverity.SUCCESS, 'Ebay category saved.');
    },
    [category, dispatch, notify],
  );

  const onFinishSuggestion = useCallback(
    ({ marketplace, categoryName }: IFormSearch): void => {
      if (marketplace.defaultCategoryId) {
        notify(
          ENotificationSeverity.SUCCESS,
          'Ebay category suggestions requested.',
        );
        dispatch(ebayCategorySuggestions(marketplace.code, categoryName));
      } else {
        notify(
          ENotificationSeverity.ERROR,
          `Ebay category request failed - Marketplace hasn't set defaultCategoryId.`,
        );
      }
    },
    [dispatch, notify],
  );

  useEffect(() => {
    marketplaces.forEach(({ name, isActive }: IEbayMarketplace): void => {
      if (isActive && category) {
        dispatch(ebayCategoryLoad(category.uuid, name));
      }
    });
  }, [category, dispatch, marketplaces]);

  useEffect((): void => {
    if (!category) {
      setMarketplacesElements([]);
      return;
    }

    const activeMarketplaces: IEbayMarketplace[] = marketplaces
      .filter(({ isActive }: IEbayMarketplace): boolean => isActive)
      .sort((a: IEbayMarketplace, b: IEbayMarketplace): number =>
        stringCompare(a.name, b.name),
      );

    const marketplaceElementsUE: React.ReactElement[] = activeMarketplaces.map(
      (marketplace: IEbayMarketplace): React.ReactElement => {
        let ebayCategoryId: string | undefined;
        let ebayCategoryName: string | undefined;

        const marketplaceCategories: Record<string, ICategoryEbay[]> =
          ebayCategories[marketplace.code];

        if (marketplaceCategories) {
          const categories: ICategoryEbay[] =
            marketplaceCategories[category.uuid];

          if (categories && categories.length) {
            const [ebayCategory] = categories;
            ebayCategoryId = ebayCategory.ebayId;
            ebayCategoryName = ebayCategory.name;
          }
        }

        const categoryNameObj: ITranslation | undefined = category.names.find(
          (name: ITranslation): boolean =>
            name.code === marketplace.languageType.code,
        );

        const categoryName: string | undefined = categoryNameObj?.value;

        const suggestions: IEbayCategoryNode[] =
          ebayCategorySuggestionsState[marketplace.code];

        let selectOptions: DefaultOptionType[] = [];

        if (suggestions && Object.keys(suggestions).length > 0) {
          selectOptions = suggestions.map(
            (suggestion: IEbayCategoryNode): DefaultOptionType => {
              let prefixName: string[] = [];

              if (suggestion.categoryTreeNodeAncestors) {
                suggestion.categoryTreeNodeAncestors.sort(
                  (
                    a: IEbayCategoryTreeNodeAncestor,
                    b: IEbayCategoryTreeNodeAncestor,
                  ): number =>
                    a.categoryTreeNodeLevel - b.categoryTreeNodeLevel,
                );

                prefixName = suggestion.categoryTreeNodeAncestors.map(
                  ({
                    categoryName: cn,
                  }: IEbayCategoryTreeNodeAncestor): string => cn,
                );
              }

              const name = `${prefixName.join(' > ')} > ${
                suggestion.category.categoryName
              }`;

              return {
                value: suggestion.category.categoryId,
                label: name,
              };
            },
          );
        }

        if (!suggestions && ebayCategoryId && ebayCategoryName) {
          selectOptions.push({
            value: ebayCategoryId,
            label: ebayCategoryName,
          });
        }

        return (
          <Row gutter={SPACING.lg} key={marketplace.code}>
            <Col span={24}>
              <Link
                to={`/ebay/marketplace/${marketplace.code}`}
                openInNewTab
                label={marketplace.name}
              />
            </Col>

            <Col span={6}>
              <Form
                form={formSearch}
                disabled={ebayInProcess}
                initialValues={{
                  categoryName,
                  marketplace,
                }}
                onFinish={onFinishSuggestion}
              >
                <Form.Item name="marketplace" hidden>
                  <Input type="hidden" />
                </Form.Item>

                <Row gutter={SPACING.lg}>
                  <Col span={20}>
                    <Form.Item
                      name="categoryName"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="Category localized name for search on Ebay"
                      />
                    </Form.Item>
                  </Col>

                  <Col span={4}>
                    <Button
                      size="large"
                      color="primary"
                      htmlType="submit"
                      disabled={ebayInProcess || !marketplace.defaultCategoryId}
                    >
                      <SearchOutlined />
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Col>

            <Col span={18}>
              <Form
                form={formAssign}
                key={marketplace.code}
                onFinish={(values: IFormAssign): void =>
                  onFinish(marketplace, values)
                }
                initialValues={{
                  ebayCategoryId,
                }}
                disabled={ebayInProcess || !marketplace.defaultCategoryId}
              >
                <Row gutter={SPACING.lg}>
                  <Col span={20}>
                    <Form.Item name="ebayCategoryId">
                      <Select size="large" options={selectOptions} />
                    </Form.Item>
                  </Col>

                  <Col span={4}>
                    <Button
                      type="primary"
                      size="large"
                      color="primary"
                      htmlType="submit"
                      disabled={ebayInProcess || !marketplace.defaultCategoryId}
                    >
                      Save
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        );
      },
    );

    setMarketplacesElements(marketplaceElementsUE);
  }, [
    category,
    dispatch,
    ebayCategories,
    ebayCategorySuggestionsState,
    ebayInProcess,
    marketplaces,
    formAssign,
    formSearch,
    onFinish,
    onFinishSuggestion,
  ]);

  return (
    <>{marketplaces.length ? marketplacesElements : <Skeleton active />}</>
  );
}

export default EbayCategories;
